/**
 * Orders status helpers — canonical normalization and bulk-action eligibility
 * predicates, reused verbatim from the legacy Orders status logic (casing
 * normalization, terminal/error sets, can-cancel/hold/resume predicates).
 */

import type { BulkActionType } from '@features/workflow/api';
import { statusKey } from '@/lib/statusPalette';

// Canonical statuses for display logic (kebab-friendly lowercase form).
export const STATUS = {
  OPEN: 'open',
  HOLD: 'hold',
  ERROR: 'error',
  ADDRESS_ERROR: 'addresserror',
  IMAGE_ERROR: 'imageerror',
  FULFILLMENT_ERROR: 'fulfillmenterror',
  PENDING: 'pending',
  CUSTOMER_CARE_HOLD: 'customercarehold',
  REVIEW_HOLD: 'reviewhold',
  READY_FOR_FULFILLMENT: 'readyforfulfillment',
  IN_PRODUCTION: 'inproduction',
  PARTIAL: 'partial',
  CANCELED: 'canceled',
  FULFILLED: 'fulfilled',
  MANUAL_HOLD: 'manualhold',
  FULFILLMENT_HOLD: 'fulfillmenthold',
  PERSONALIZATION_HOLD: 'personalizationhold',
} as const;

// Server emits a mix of casings (PascalCase from .NET, occasional camelCase,
// lowercase). `normalizeStatus` collapses all of that to a stable lowercase
// form for predicate logic.
//
// MANUAL_HOLD is deliberately NOT aliased onto CUSTOMER_CARE_HOLD. The two share
// the "On Hold" pill, but they must stay separable: only one of them can be
// cleared by the user who set it, they carry different history reasons, and
// customer care outranks manual in the hold-precedence order. Note the alias
// below matches the whole flattened string, so `manualhold` never hits it.
const ALIASES: Record<string, string> = {
  hold: STATUS.CUSTOMER_CARE_HOLD, // legacy short form for Customer Care Hold
};

export function normalizeStatus(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const flat = statusKey(raw);
  if (ALIASES[flat]) return ALIASES[flat];
  // Unknown values pass through as-is so they render gracefully.
  return flat;
}

// PascalCase form expected by `GET /v1/orders?orderStatuses=…`.
const TO_API: Record<string, string> = {
  [STATUS.OPEN]: 'Open',
  [STATUS.HOLD]: 'Hold',
  [STATUS.ERROR]: 'Error',
  [STATUS.ADDRESS_ERROR]: 'AddressError',
  [STATUS.IMAGE_ERROR]: 'ImageError',
  [STATUS.FULFILLMENT_ERROR]: 'FulfillmentError',
  [STATUS.PENDING]: 'Pending',
  [STATUS.CUSTOMER_CARE_HOLD]: 'CustomerCareHold',
  [STATUS.REVIEW_HOLD]: 'ReviewHold',
  [STATUS.READY_FOR_FULFILLMENT]: 'ReadyForFulfillment',
  [STATUS.IN_PRODUCTION]: 'InProduction',
  [STATUS.PARTIAL]: 'Partial',
  [STATUS.CANCELED]: 'Canceled',
  [STATUS.FULFILLED]: 'Fulfilled',
  [STATUS.MANUAL_HOLD]: 'ManualHold',
  [STATUS.FULFILLMENT_HOLD]: 'FulfillmentHold',
  [STATUS.PERSONALIZATION_HOLD]: 'PersonalizationHold',
};

export function toApiStatus(s: string | null | undefined): string | null {
  const n = normalizeStatus(s);
  return (n && TO_API[n]) || null;
}

// Terminal statuses can't be acted on (no Cancel, no Hold, no Resume).
export const TERMINAL_STATUSES = new Set<string>([STATUS.CANCELED, STATUS.FULFILLED]);

// Error statuses that Resume Processing applies to.
export const ERROR_STATES = new Set<string>([
  STATUS.IMAGE_ERROR,
  STATUS.FULFILLMENT_ERROR,
  STATUS.ADDRESS_ERROR,
  STATUS.ERROR,
]);

// Statuses where the Inspector should fetch + render shipment tracking.
export const SHIPMENT_VISIBLE_STATUSES = new Set<string>([
  STATUS.IN_PRODUCTION,
  STATUS.READY_FOR_FULFILLMENT,
  STATUS.FULFILLED,
  STATUS.FULFILLMENT_ERROR,
]);

export function isTerminal(s: string | null | undefined): boolean {
  const n = normalizeStatus(s);
  return n != null && TERMINAL_STATUSES.has(n);
}

export function canCancelOrder(s: string | null | undefined): boolean {
  return !isTerminal(s);
}

/**
 * Every status a manual hold can be placed from.
 *
 * An allow-list, not a list of exclusions, because the two fail differently when
 * incomplete: a status missing from a deny-list becomes holdable by accident,
 * where one missing from here merely cannot be held until someone adds it.
 *
 * The test is not "is this pre-production" but "is the order still moving". A
 * hold pauses work in flight, so it can do nothing for an order that has already
 * stopped. Only OPEN and READY_FOR_FULFILLMENT qualify, which is exactly the pair
 * the PRD names.
 *
 * That is narrower than pre-production on purpose. The error statuses sort as
 * pre-production but each is stopped, waiting on a person: IMAGE_ERROR blocks the
 * whole order, and FULFILLMENT_ERROR is an end status that can even sit over
 * shipments already in production. Resume Processing is the route back for those,
 * not Hold. PENDING is out for the same reason: the systematic timed hold is
 * itself a hold, so the order is already parked. Past IN_PRODUCTION the vendor
 * owns the shipments and Product ruled out an admin override.
 */
export const HOLDABLE_STATUSES = new Set<string>([
  STATUS.OPEN,
  STATUS.READY_FOR_FULFILLMENT,
]);

/**
 * Whether a manual hold can be placed on an order in this status.
 *
 * Status is only half the answer: the caller must also hold the Manage Order
 * Pre-Production permission, and the server re-checks eligibility at confirm
 * time (it rejects a hold while an order-processing lock is active). This
 * predicate exists so the control is hidden rather than offered-then-refused.
 *
 * Already-held orders fall out for free: neither CUSTOMER_CARE_HOLD nor
 * MANUAL_HOLD is in the set. A manual hold has nothing to add to itself, and
 * customer care outranks manual in the precedence order. An unrecognized status
 * is not holdable for the same reason: `normalizeStatus` passes unknown values
 * through, and they will not match.
 *
 * FULFILLMENT_HOLD is excluded by the same omission, and must stay that way:
 * order-data-service refuses a manual hold on top of it
 * (`ManualHoldEligibility.IsHigherPrecedenceHold`), so offering the control
 * would show a button that always fails.
 */
export function canHoldOrder(s: string | null | undefined): boolean {
  const n = normalizeStatus(s);
  return n != null && HOLDABLE_STATUSES.has(n);
}

/** Whether an active manual hold on this order can be released by the user. */
export function canResumeManualHold(s: string | null | undefined): boolean {
  return normalizeStatus(s) === STATUS.MANUAL_HOLD;
}

export function isPersonalizationHeld(s: string | null | undefined): boolean {
  return normalizeStatus(s) === STATUS.PERSONALIZATION_HOLD;
}

export function canResumeProcessing(s: string | null | undefined): boolean {
  const n = normalizeStatus(s);
  return n != null && ERROR_STATES.has(n);
}

// Row checkbox is actionable when at least one bulk action applies to the row.
export function isRowActionable(s: string | null | undefined): boolean {
  return canCancelOrder(s) || canHoldOrder(s) || canResumeProcessing(s);
}

/**
 * Per-status bulk-action matrix — verbatim parity with the legacy pre-V2
 * Orders page (`main:src/app/pages/Orders/OrdersView/index.js` `STATUS_ACTIONS`,
 * the epic status-action matrix). Keys are normalized lowercase
 * statuses; each value is the set of actions a single order in that status
 * permits. Statuses with no entry (AddressError, ReviewHold, Partial) permit
 * NO bulk action — matching the legacy matrix exactly. `STATUS.HOLD` normalizes
 * to CUSTOMER_CARE_HOLD via `normalizeStatus`, so its row is never matched, but
 * it's kept here for fidelity with the legacy map.
 */
export const STATUS_ACTIONS: Record<string, BulkActionType[]> = {
  [STATUS.PENDING]: ['ResumeProcessing', 'Cancel'],
  [STATUS.OPEN]: ['ResumeProcessing', 'Cancel'],
  [STATUS.IMAGE_ERROR]: ['ResumeProcessing', 'Cancel'],
  [STATUS.CUSTOMER_CARE_HOLD]: ['ResumeProcessing', 'Cancel'],
  [STATUS.HOLD]: ['ResumeProcessing', 'Cancel'],
  [STATUS.READY_FOR_FULFILLMENT]: ['SendToVendor', 'Cancel'],
  [STATUS.FULFILLMENT_ERROR]: ['SendToVendor', 'Cancel'],
  [STATUS.ERROR]: ['Cancel'],
  [STATUS.IN_PRODUCTION]: ['RetryPolling', 'Cancel'],
  [STATUS.FULFILLED]: [],
  [STATUS.CANCELED]: [],
  // Cancel only, and the omission of ResumeProcessing is the point: a merchant fulfillment
  // hold is lifted by removing the merchant's hold, never per order, so Resume must not be
  // offered to any role (PROD-945). An explicit ['Cancel'] rather than leaving the row out,
  // because an absent status permits no action at all - and Product confirmed a held order
  // stays cancellable, since cancelling does not fulfil anything.
  [STATUS.FULFILLMENT_HOLD]: ['Cancel'],
  // Resume releases the hold through the same endpoint as the single-order Resume, which
  // orchestrator-service's bulk engine calls for this status.
  [STATUS.PERSONALIZATION_HOLD]: ['ResumeProcessing', 'Cancel'],
};

/** Bulk actions a single order's status permits (normalized matrix lookup). */
export function statusActions(status: string | null | undefined): BulkActionType[] {
  const n = normalizeStatus(status);
  return (n && STATUS_ACTIONS[n]) || [];
}

/** Whether a single order's status permits a given bulk action. */
export function canDoAction(action: BulkActionType, status: string | null | undefined): boolean {
  return statusActions(status).includes(action);
}

/**
 * Bulk actions offered for a multi-order selection — the INTERSECTION of each
 * selected order's permitted actions (legacy `getAvailableActions` parity: an
 * action shows only when EVERY selected order supports it). A terminal order
 * permits no bulk action, so including one in the selection correctly
 * collapses the intersection to empty — it must NOT be dropped from `sets`
 * before intersecting, or its empty set stops constraining the result.
 */
export function getAvailableActions(orders: Array<{ status?: string | null }>): BulkActionType[] {
  const sets = orders.map((o) => statusActions(o.status));
  if (sets.length === 0) return [];
  return sets[0]!.filter((action) => sets.every((s) => s.includes(action)));
}

/**
 * Triage "why" line — resolves to an i18n KEY PATH (relative to the `orders`
 * namespace); callers wrap the result in `t(...)`. `isPollingStopped`
 * overrides the status-based reason, mirroring the legacy OrderMessageCard.
 * Used by the Triage rail (Phase 2).
 */
export const TRIAGE_REASON: Record<string, string> = {
  [STATUS.IMAGE_ERROR]: 'list.triage.reason.imageError',
  [STATUS.FULFILLMENT_ERROR]: 'list.triage.reason.fulfillmentError',
  [STATUS.CUSTOMER_CARE_HOLD]: 'list.triage.reason.customerCareHold',
  [STATUS.MANUAL_HOLD]: 'list.triage.reason.manualHold',
  [STATUS.PERSONALIZATION_HOLD]: 'list.triage.reason.personalizationHold',
};

export function triageReason(order: {
  status?: string | null;
  isPollingStopped?: boolean | null;
} | null | undefined): string {
  if (!order) return 'list.triage.reason.default';
  if (order.isPollingStopped) return 'list.triage.reason.pollingStopped';
  const n = normalizeStatus(order.status);
  return (n && TRIAGE_REASON[n]) || 'list.triage.reason.default';
}
